import { LightningElement } from 'lwc';

import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import { patch } from 'aws-amplify/api';

import {API_BASE_URL} from '../../../utils/awsUtils';
import { mapTemplateLabels, TEMPLATE_LABELS } from '../../../utils/languageUtil';
import { IS_TESTING_NO_INTERNET } from '../../../utils/basicUtils';
import {
    clearHeaderCache,
    getHeaderData,
    HEADER_DATA_CHANGE_EVENT,
    notifyHeaderDataChanged,
    updateHeaderDataCache,
} from '../../../utils/headerDataCache';

import {
    ADMIN_OPTIONS,
    USER_OPTIONS,
    DESTINATION_OPTIONS,
    GLOBAL_ACTION_OPTIONS_NON_USER,
    GLOBAL_ACTION_OPTIONS_USER,
    EVENT_OPTIONS,
} from '../../../utils/optionsUtil';

import {
    getStoredLocation,
    requestAndStoreLocation,
    getStoredApproxLocation,
    requestAndStoreApproxLocation,
} from '../../../utils/locationUtils';

const GOOGLE_MAPS_API_KEY = 'AIzaSyC4Vr-YUcad614oCkjf4ju5n6xwQdUm6HQ';

let googleMapsBootstrapPromise;
let googleMapsApiPromise;

function loadGoogleMapsScript() {
    if (window.google?.maps?.importLibrary) {
        return Promise.resolve();
    }

    if (googleMapsBootstrapPromise) {
        return googleMapsBootstrapPromise;
    }

    googleMapsBootstrapPromise = Promise.resolve().then(() => {
        const googleNamespace = window.google = window.google || {};
        const mapsNamespace = googleNamespace.maps = googleNamespace.maps || {};
        const libraries = new Set();
        const callbackName = '__ib__';

        if (mapsNamespace.importLibrary) {
            return;
        }

        const loadApi = () => {
            if (!googleMapsApiPromise) {
                googleMapsApiPromise = new Promise((resolve, reject) => {
                    const params = new URLSearchParams();
                    const script = document.createElement('script');

                    params.set('libraries', [...libraries].join(','));
                    params.set('key', GOOGLE_MAPS_API_KEY);
                    params.set('v', 'weekly');
                    params.set('loading', 'async');
                    params.set('callback', `google.maps.${callbackName}`);

                    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
                    script.async = true;
                    script.onerror = () => {
                        googleMapsApiPromise = null;
                        reject(new Error('The Google Maps JavaScript API could not load.'));
                    };
                    script.nonce = document.querySelector('script[nonce]')?.nonce || '';
                    mapsNamespace[callbackName] = resolve;

                    document.head.appendChild(script);
                });
            }

            return googleMapsApiPromise;
        };

        mapsNamespace.importLibrary = (libraryName, ...rest) => {
            libraries.add(libraryName);
            return loadApi().then(() => mapsNamespace.importLibrary(libraryName, ...rest));
        };
    });

    return googleMapsBootstrapPromise;
}


export default class App extends LightningElement {
    // ⚠️ Watch this --> set to false when have internet
    isTest = IS_TESTING_NO_INTERNET;
    templateLabels = mapTemplateLabels(TEMPLATE_LABELS);
    _isLoading = false;
    

    // ==================== Action Options ==================== \\
    adminOptions = ADMIN_OPTIONS;
    destinationOptions = DESTINATION_OPTIONS;
    eventOptions = EVENT_OPTIONS;
    unreadMessageCount = 0;
    get globalActionOptions() {
        if (!this.user) {
            return  GLOBAL_ACTION_OPTIONS_NON_USER;
        }
        return GLOBAL_ACTION_OPTIONS_USER;
    }

    get userOptions() {
        return USER_OPTIONS.map(option => {
            if (option.value !== 'messages') {
                return option;
            }

            return {
                ...option,
                label: this.unreadMessageCount > 0
                    ? `Messages (${this.unreadMessageCount} new)`
                    : 'Messages'
            };
        });
    }

    get userProfilePhotoUrl() {
        return this.sessionUser?.profile_photo_url || this.user?.profile_photo_url || this.user?.picture || null;
    }

    get hasUserProfilePhoto() {
        return !!this.userProfilePhotoUrl;
    }

    get userProfilePhotoAlt() {
        const name = this.sessionUser?.display_name || this.sessionUser?.first_name || 'User';
        return `${name} profile photo`;
    }

    get isUserSignedIn() {
        return !!this.user;
    }

    get hasUnreadMessages() {
        return this.unreadMessageCount > 0;
    }

    get unreadMessageBadgeLabel() {
        return String(this.unreadMessageCount);
    }
    // ======================================================== \\

    
    
    //📍 ======================== Get User Location ========================== 📍\\
    getGooglePlaceLocationFilter(place, latitude, longitude) {
        try {
            const mappedLocation = {
                city: null,
                state: null,
                country: null,
                latitude,
                longitude
            };

            (place?.address_components || []).forEach(component => {
                const types = component.types || [];

                if (types.includes('locality')) {
                    mappedLocation.city = component.long_name;
                }

                if (!mappedLocation.city && types.includes('postal_town')) {
                    mappedLocation.city = component.long_name;
                }

                if (!mappedLocation.city && types.includes('administrative_area_level_2')) {
                    mappedLocation.city = component.long_name;
                }

                if (types.includes('administrative_area_level_1')) {
                    mappedLocation.state = component.short_name;
                }

                if (types.includes('country')) {
                    mappedLocation.country = component.short_name;
                }
            });
            console.log('here is mappedLocation in getGooglePlaceLocationFilter() of app.js: ', mappedLocation);
            return mappedLocation;

        } catch(error) {
            this.showToast('Error in getGooglePlaceLocationFilter() of app.js', error, 'error');
            return null;
        } 
    }
    async getApproxLocationFromGooglePlaces(location) {
        try {
            const latitude = Number(location?.lat);
            const longitude = Number(location?.lng);

            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                return null;
            }
            
            await loadGoogleMapsScript();

            const { Geocoder } = await google.maps.importLibrary('geocoding');
            const geocoder = new Geocoder();

            return new Promise((resolve, reject) => {
                geocoder.geocode(
                    {
                        location: {
                            lat: latitude,
                            lng: longitude
                        }
                    },
                    (results, status) => {
                        if (status !== 'OK' || !results?.length) {
                            reject(status);
                            return;
                        }

                        resolve(this.getGooglePlaceLocationFilter(results[0], latitude, longitude));
                    }
                );
            });

        } catch(error) {
            this.showToast('Error in getApproxLocationFromGooglePlaces() of app.js', error, 'error');
            return null;
        } 
    }
    userLocationPrecise;
    async getPreciseLocation() {
        this.userLocationPrecise = getStoredLocation();

        if (!this.userLocationPrecise) {
            try {
                this.userLocationPrecise = await requestAndStoreLocation();
                if (this.userLocationPrecise) {
                    console.log('Here is the location in initPage() of getWetHeaderSearchBar.js: ', this.userLocationPrecise);
                }
            }
            catch (error) {
                console.error(error);
                return;
            }
        }
    }
    // ⭐️ Gives you city, location, region, lat, lng 
    userLocationApprox; 
    async getUserLocationApprox() {
        this.userLocationApprox = await getStoredApproxLocation();

        if (!this.userLocationApprox) {
            this.userLocationApprox = await requestAndStoreApproxLocation();
        }

        console.log('Approximate location: ', this.userLocationApprox);
    }
    //📍 ===================================================================== 📍\\
    
    user = IS_TESTING_NO_INTERNET ? {id: '12191292'} : null;
    _idToken;
    sessionUser;
    listings = [];
    mq;
    // ================ Check if user signed in =============== \\
    applyHeaderData(headerData = {}) {
        if ('user' in headerData) {
            this.user = headerData.user || null;
        }
        if ('sessionUser' in headerData) {
            this.sessionUser = headerData.sessionUser || null;
        }
        if ('idToken' in headerData) {
            this._idToken = headerData.idToken || null;
        }
        if ('unreadMessageCount' in headerData) {
            this.unreadMessageCount = Number(headerData.unreadMessageCount || 0);
        }
    }

    // Get user basic info on sign-in
    async loadSessionUser() {
        this._isLoading = true;
        try {
            const headerData = await getHeaderData();
            this.applyHeaderData(headerData);
        } catch(error) {
            this.showToast('Error in loadSessionUser() of app.js', error, 'error');
        } finally {
            this._isLoading = false;
        }

    }
    handleProfileUpdated(message) {
        const updatedUser = message.detail?.user || message.detail || null;
        if (!updatedUser) return;

        this.sessionUser = {
            ...this.sessionUser,
            ...updatedUser
        };
        updateHeaderDataCache({ sessionUser: this.sessionUser });
        notifyHeaderDataChanged();
    }
    handleUnreadMessagesChange(message) {
        this.unreadMessageCount = Number(message.detail?.unreadMessageCount || 0);
        updateHeaderDataCache({ unreadMessageCount: this.unreadMessageCount });
        notifyHeaderDataChanged();
    }
    async connectedCallback() {
        this.syncPageFromUrl();
        window.addEventListener('popstate', this.onPopState);
        window.addEventListener(HEADER_DATA_CHANGE_EVENT, this.onHeaderDataChanged);

        // 1) create the query
        this.mq = window.matchMedia('(max-width: 600px)');

        // 2) attach listener (new + fallback)
        if (this.mq.addEventListener) {
            this.mq.addEventListener('change', this.onMqChange);
        } else {
            this.mq.addListener(this.onMqChange);
        }

        // 3) initial evaluation (Chrome device toolbar won’t fire 'change' until a threshold is crossed)
        this.onMqChange(this.mq);

        try {
            await this.getUserLocationApprox();

            const approxLocation = this.userLocationApprox || {};
            
            const googleLocation = await this.getApproxLocationFromGooglePlaces(approxLocation)
                .catch((error) => {
                    console.error('Google reverse geocode failed in app.js:', error);
                    return null;
                });

            console.log('here is google location in connectedCallback() of app.js: ', googleLocation);

            await this.loadListings({
                city: googleLocation?.city || '',
                state: googleLocation?.state || '',
                country: googleLocation?.country || '',
                latitude: approxLocation.lat ?? '',
                longitude: approxLocation.lng ?? '',
                guests: this._filterForm.guests ?? '',
                date: this._filterForm.date ?? ''
            });
        } catch (e) {
            console.error('loadListings failed:', e);
        }

        try {
            if (!this.isTest) {
                await this.loadSessionUser();
            }
        } catch (e) {
            console.error('loadSessionUser failed:', e);
        }

    }
    disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
        window.removeEventListener(HEADER_DATA_CHANGE_EVENT, this.onHeaderDataChanged);
        if (!this.mq) return;
        if (this.mq.removeEventListener) {
            this.mq.removeEventListener('change', this.onMqChange);
        } else {
            this.mq.removeListener(this.onMqChange);
        }
    }
    // ======================================================== \\

    
    // =================== Log in / Log out ================== \\
    // Trigger log in
    handleLoginSignup() {
        this.template.querySelector('x-get-wet-sign-up-modal').open();
    }
    handleSignup() {
        this.template.querySelector('x-get-wet-sign-up-modal').open({ signUp: true });
    }
    // Handle log in
    async handleUserAuthenticated(message) {
        this._isLoading = true;
        try {
            this.user = message.detail.user;
            const profile = message.detail?.profile;
            const modal = this.template.querySelector('x-get-wet-sign-up-modal');
            if (modal) {
                modal.close();
            }
            if (profile?.first_name && profile?.last_name) {
                await this.saveSessionUserProfile(profile);
            }
            clearHeaderCache();
            await this.loadSessionUser();
            notifyHeaderDataChanged();
        } catch(error) {
            this.showToast('Error in handleUserAuthenticated() of app.js', error, 'error');
        } finally {
            this._isLoading = false;
        }
    }
    // Handle log out
    async handleSignOut() {
        this._isLoading = true;
        try {
            await signOut();
            this.user = null;
            this.sessionUser = null;
            this._idToken = null;
            this.unreadMessageCount = 0;
            clearHeaderCache();
            notifyHeaderDataChanged({
                user: null,
                sessionUser: null,
                idToken: null,
                unreadMessageCount: 0
            });
        } catch(error) {
            this.showToast('Error in handleSignOut() of app.js', error, 'error');
        } finally {
            this._isLoading = false;
        }
    }

    async saveSessionUserProfile(fields) {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        if (!idToken) {
            throw new Error('No idToken available to save profile.');
        }

        const op = patch({
            apiName: 'api',
            path: '/cognito/me',
            options: {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: fields
            }
        });

        await op.response;
    }

    currentListingId = null;
    currentHostListingId = null;
    currentCheckoutSessionId = null;
    currentPaymentCancelled = false;
    currentOpenMessageHost = false;

    syncPageFromUrl() {
        const params = new URLSearchParams(window.location.search);
        this.currentListingId = params.get('listingId');
        this.currentHostListingId = params.get('hostListingId');
        this.currentCheckoutSessionId = params.get('session_id');
        this.currentPaymentCancelled = ['1', 'true', 'yes'].includes(
            String(params.get('paymentCancelled') || '').toLowerCase()
        );
        this.currentOpenMessageHost = ['1', 'true', 'yes'].includes(
            String(params.get('messageHost') || '').toLowerCase()
        );
        this._filterForm = {
            ...this._filterForm,
            date: params.get('date') || this._filterForm.date,
            guests: params.get('guests') ? Number(params.get('guests')) : this._filterForm.guests
        };
        this.updatePageBools(this.currentListingId ? null : params.get('page'));
    }
    buildListingUrl(listingId) {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        url.searchParams.delete('hostListingId');
        url.searchParams.delete('session_id');
        url.searchParams.delete('paymentCancelled');
        url.searchParams.delete('messageHost');
        url.searchParams.set('listingId', listingId);

        if (this._filterForm.date) {
            url.searchParams.set('date', this._filterForm.date);
        }
        if (this._filterForm.guests) {
            url.searchParams.set('guests', this._filterForm.guests);
        }

        return url.toString();
    }
    buildPageUrl(page, params = {}) {
        const url = new URL(window.location.href);
        url.searchParams.delete('listingId');
        url.searchParams.delete('hostListingId');
        url.searchParams.delete('session_id');
        url.searchParams.delete('paymentCancelled');
        url.searchParams.delete('messageHost');

        if (page) {
            url.searchParams.set('page', page);
        } else {
            url.searchParams.delete('page');
        }

        Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                url.searchParams.delete(key);
            } else {
                url.searchParams.set(key, value);
            }
        });

        return url.toString();
    }
    buildMessageCenterUrl() {
        return this.buildPageUrl('messages');
    }
    navigateToPage(page, params = {}) {
        this.currentListingId = null;
        this.currentHostListingId = params.hostListingId || null;
        this.currentCheckoutSessionId = params.session_id || null;
        this.currentPaymentCancelled = false;
        this.currentOpenMessageHost = false;
        window.history.pushState({}, '', this.buildPageUrl(page, params));
        this.updatePageBools(page);
    }
    // Navigate user to the listing when card is clicked 
    handleCardClick(e) {
        try {   
            const listingId = e?.currentTarget?.dataset?.id;
            if (listingId) {
                window.open(this.buildListingUrl(listingId), '_blank', 'noopener');
            }
        } catch(error) {
            this.showToast('Error in handleCardClick() of app.js', error, 'error');
        } 
    }
    // ======================================================== \\



    _filterForm = {
        city: null,
        state: null,
        country: null,
        date: null,
        guests: null,
        latitude: null,
        longitude: null,
        searchMode: null
    };
    getListingSearchParams(filters = {}) {
        const baseParams = {
            city: null,
            state: null,
            country: null,
            latitude: filters.latitude,
            longitude: filters.longitude,
            guests: filters.guests,
            date: filters.date
        };
        const searchMode = String(filters.searchMode || '')
            .toLowerCase()
            .replace(/\s+/g, '_');

        if (!searchMode || searchMode === 'city') {
            return {
                ...baseParams,
                city: filters.city,
                state: filters.state,
                country: filters.country
            };
        }

        if (searchMode === 'country') {
            return {
                ...baseParams,
                country: filters.country
            };
        }

        return baseParams;
    }
    // Filter listings by city/state/guests/availability
    async filterSearch() {
        try {
            await this.loadListings(this.getListingSearchParams(this._filterForm));
        } catch(error) {
            console.error('Error in filterSearch() of app.js', error);
            this.showToast('Search Failed', error.message || 'Unable to search listings.', 'error');
        }
    }
    async handleSearchListings(message) {
        this._filterForm = {
            ...this._filterForm,
            ...(message.detail || {})
        };

        this.handleHomePageClick();

        this._isLoading = true;
        try {
            await this.loadListings(this.getListingSearchParams(this._filterForm));
        } catch(error) {
            console.error('Error in handleSearchListings() of app.js', error);
            this.showToast('Search Failed', error.message || 'Unable to search listings.', 'error');
        } finally {
            this._isLoading = false;
        }
    }
    // Load listings when the page loads
    async loadListings({ city, state, country, latitude, longitude, guests, date } = {}) {
        const safeDate = date || new Date().toISOString().slice(0, 10);

        const params = new URLSearchParams({
            city: city || '',
            state: state || '',
            country: country || '',
            latitude: latitude === null || latitude === undefined ? '' : String(latitude),
            longitude: longitude === null || longitude === undefined ? '' : String(longitude),
            guests: String(guests || 1),
            date: safeDate
        });


        const res = await fetch(`${API_BASE_URL}/public/listings?${params.toString()}`, {
            method: 'GET'
        });

        if (!res.ok) {
            const errText = await res.text();
            throw new Error(`Failed to fetch listings: ${res.status} ${errText}`);
        }

        const data = await res.json();
        console.log('here is listings in loadListings of app.js: ', data.listings);

        this.listings = data?.listings?.length 
            ? data.listings.map(l => ({...l, isImageLoading: true, hasImageError: false})) 
            : [];
    }
    // Ditch spinner when image loads
    handleImageLoad(e) {
        const imageId = e.target.dataset.id;
        this.listings = this.listings.map(l =>
            l.id === imageId
                ? { ...l, isImageLoading: false }
                : l
        );
    }
    // Ditch spinner on error and flag error
    handleImageError(e) {
        const imageId = e.target.dataset.id;
        console.log('in handleImageError() of app.js, here is image id: ', imageId);
        this.listings = this.listings.map(l =>
            l.id === imageId
                ? { ...l, isImageLoading: false, hasImageError: true }
                : l
        );
    }
    
    _destination;
    minReservationDate = new Date();
    // ================= Handle Header Inputs ================= \\
    handleDestination(message) {
        const destination = message.detail;
        if (!destination) {
            this._destination = null;
            this._filterForm = {
                ...this._filterForm,
                city: null,
                state: null,
                country: null,
                latitude: null,
                longitude: null
            };
            return;
        }

        if (typeof destination === 'object') {
            this._destination = [destination.city, destination.state].filter(Boolean).join(', ');
            this._filterForm = {
                ...this._filterForm,
                city: destination.city || null,
                state: destination.state || null,
                country: destination.country || null,
                latitude: destination.latitude ?? null,
                longitude: destination.longitude ?? null
            };
            return;
        }

        const [city, state, country] = destination.split(':');
        this._destination = `${city}, ${state}`;
        console.log('destination in handleDestination() of app.js: ', this._destination);
        this._filterForm = {...this._filterForm, city, state, country};
    }
    handleReservationDate(message) {
        const value = message.detail?.value;
        this._filterForm = {
            ...this._filterForm,
            date: value
        };
    }
    handleGuests(message) {
        this._filterForm = {...this._filterForm, guests: message.detail.value};
    }
    // ======================================================== \\



    // ==================== Admin Handlers ==================== \\
    get isAdminUser() {
        return this.sessionUser?.role === 'admin'
    }
    // Navigate to admin page
    handleAdminAction(message) {
        const action = message.detail.value;
        switch(action) {
            case 'Listing Approvals':
                this.sessionUser = {...this.sessionUser, idToken: this._idToken}
                this.navigateToPage('listingApprovals');
                return;
            case 'Flagged Listing':
                this.navigateToPage('flaggedListings');
                return;
            default: 
                return;
        }
    }
    // ======================================================== \\



    // Hide date/destination input bar
    get showHeaderSearchBar() {
        return !this.isBecomeHostActive && !this.isListingEditActive;
    }
    get hideSearchWrapper() {
        if (
            this.isAccountDetailsActive || 
            this.isGuestActivityActive ||
            this.isBookingSuccessActive ||
            this.isListingPageActive || 
            this.isMessageCenterActive || 
            this.isHelpCenterActive || 
            this.isHostHomePageActive ||
            this.isListingEditActive
        ) {
            return true; 
        }
        return false;
    }
    get isListingPageActive() {
        return !!this.currentListingId;
    }
    // ------------- Page Booleans ------------- \\
    isBecomeHostActive = false;
    isListingEditActive = false;
    isAccountDetailsActive = false;
    isGuestActivityActive = false;
    isHostHomePageActive = false;
    isMessageCenterActive = false;
    isHelpCenterActive = false;
    isBookingSuccessActive = false;
    _isListingApprovals = false;
    _isFlaggedListings = false;
    // ----------------------------------------- \\
    
    // Active page controller
    updatePageBools(activePage) {
        this.isBecomeHostActive = activePage === 'becomeHost'
        this.isListingEditActive = activePage === 'listingEdit';
        this.isAccountDetailsActive = activePage === 'accountDetails';
        this.isGuestActivityActive = activePage === 'activity';
        this.isHostHomePageActive = activePage === 'hostHomePage';
        this.isMessageCenterActive = activePage === 'messages';
        this.isHelpCenterActive = activePage === 'helpCenter';
        this.isBookingSuccessActive = activePage === 'bookingSuccess';
        this._isListingApprovals = activePage === 'listingApprovals' || activePage === 'Listing Approvals';
        this._isFlaggedListings = activePage === 'flaggedListings' || activePage === 'Flagged Listings' || activePage === 'Flagged Listing';
    }

    // Show Home Page
    handleHomePageClick() {
        this.currentListingId = null;
        this.currentHostListingId = null;
        this.currentCheckoutSessionId = null;
        this.currentPaymentCancelled = false;
        this.currentOpenMessageHost = false;
        const url = new URL(window.location.href);
        url.searchParams.delete('listingId');
        url.searchParams.delete('hostListingId');
        url.searchParams.delete('session_id');
        url.searchParams.delete('paymentCancelled');
        url.searchParams.delete('messageHost');
        url.searchParams.delete('page');
        window.history.replaceState({}, '', url.toString());
        this.updatePageBools(null);
    }
    // Show Host Sign up page
    signUpHost() {
        this.navigateToPage('becomeHost');
    }
    hideSignUpHost() {
        if (this.currentHostListingId) {
            this.showHostHomePage();
            return;
        }
        this.handleHomePageClick();
    }
    handleListingEditClose() {
        this.showHostHomePage();
    }
    // Show Account Details 
    showAccountDetails() {
        this.navigateToPage('accountDetails');
    }
    showGuestActivity() {
        this.navigateToPage('activity');
    }
    showHostHomePage() {
        this.navigateToPage('hostHomePage');
    }
    showMessageCenter() {
        this.navigateToPage('messages');
    }
    navToHelpCenter() {
        this.navigateToPage('helpCenter');
    }
    handleViewSuccessListing(message) {
        const listingId = message.detail?.listingId;
        if (!listingId) return;

        this.currentListingId = listingId;
        this.currentCheckoutSessionId = null;
        this.currentPaymentCancelled = false;
        this.currentOpenMessageHost = false;
        window.history.pushState({}, '', this.buildListingUrl(listingId));
        this.updatePageBools(null);
    }
    handleMessageSuccessHost(message) {
        const listingId = message.detail?.listingId;
        if (!listingId) return;

        this.currentListingId = listingId;
        this.currentCheckoutSessionId = null;
        this.currentPaymentCancelled = false;
        this.currentOpenMessageHost = true;

        const url = new URL(this.buildListingUrl(listingId));
        url.searchParams.set('messageHost', '1');
        window.history.pushState({}, '', url.toString());
        this.updatePageBools(null);
    }
    handleEditHostListing(message) {
        const listingId = message.detail?.listingId;
        if (!listingId) return;
        this.navigateToPage('listingEdit', {hostListingId: listingId});
    }
    handleGuestActivityViewListing(message) {
        const listingId = message.detail?.listingId;
        if (!listingId) return;

        this.currentListingId = listingId;
        this.currentCheckoutSessionId = null;
        this.currentPaymentCancelled = false;
        this.currentOpenMessageHost = false;
        window.history.pushState({}, '', this.buildListingUrl(listingId));
        this.updatePageBools(null);
    }
    handleGuestActivityContactHost(message) {
        this.currentListingId = null;
        this.currentCheckoutSessionId = null;
        this.currentPaymentCancelled = false;
        this.currentOpenMessageHost = false;
        this.navigateToPage('messages');
    }

    // User Profile Actions Handler
    async handleUserAction(message) {
        const action = message.detail.value;
        
        switch(action) {
            case 'logOut':
                await this.handleSignOut();
                return;
            case 'accountDetails':
                this.showAccountDetails();
                return
            case 'activity':
                this.showGuestActivity();
                return
            case 'messages':
                this.showMessageCenter();
                return
            case 'My Listings': 
                this.showHostHomePage();
            default:
                return;
        }
    }
    // Toggle Pages --> Handle "rows" button click
    handleGetWetMenuSelection(message) {
        const selection = message.detail.value;
        
        const selectionLower = selection && typeof selection === 'string'
            ? selection.toLowerCase()
            : null;

        switch(selectionLower) {
            case 'loginsignup':
                this.handleLoginSignup();
                break;
            case 'signup':
                this.handleSignup();
                break;
            case 'becomehost':
                this.signUpHost();
                break;
            case 'helpcenter':
                this.navToHelpCenter();
                break;
            default:
                break;
        }
    }
    // Handle "events" button click
    handleGetWetEventSelection(message) {
        console.log('Here is event message: ', JSON.stringify(message?.detail));
    }


    get adminSessionUser() {
        return this.sessionUser
            ? { ...this.sessionUser, idToken: this._idToken }
            : null;
    }

    // ================= Track Mobile Setting ================= \\
    isMobile = false;
    mq;
    onPopState = () => {
        this.syncPageFromUrl();
    };
    onHeaderDataChanged = (event) => {
        this.applyHeaderData(event.detail || {});
    };
    onMqChange = (e) => {
        // e can be MediaQueryListEvent or the MediaQueryList itself (on initial call)
        const matches = typeof e.matches === 'boolean' 
            ? e.matches 
            : e.currentTarget?.matches;
        this.isMobile = !!matches;
    };
    // ======================================================== \\

    toastObj;
    showToast(title, message, variant, mode=null) {
        const modeP = mode 
            ? mode 
                : variant === 'success'
            ? 'dismissible'
            : 'sticky';
        
        this.toastObj = { 
            variant: variant, 
            mode: modeP, 
            message: message,
            header: { 
                text: title
            }        
        };
    }
}
